import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../lib/supabaseAdmin';

const ArticleEditor = ({ articleId, onBack, initialData }) => {
    const [categories, setCategories] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        slug: '',
        content: '',
        category_id: '',
        author_id: '',
        is_featured: false,
        published_at: new Date().toISOString().slice(0, 16),
        ...initialData,
    });

    useEffect(() => {
        const fetchMetadata = async () => {
            const [catResult, authResult] = await Promise.all([
                supabaseAdmin.from('categories').select('id, name').order('name'),
                supabaseAdmin.from('authors').select('id, name').order('name'),
            ]);
            if (catResult.data) setCategories(catResult.data);
            if (authResult.data) setAuthors(authResult.data);
        };
        fetchMetadata();
    }, []);

    useEffect(() => {
        if (!articleId) return;
        const fetchArticle = async () => {
            const { data, error } = await supabaseAdmin
                .from('articles')
                .select('*')
                .eq('id', articleId)
                .single();

            if (error) {
                setStatus({ type: 'error', message: 'Failed to load article' });
                return;
            }

            // Extract HTML content from the JSON content field
            let contentHtml = '';
            if (Array.isArray(data.content) && data.content.length > 0) {
                if (data.content[0].type === 'rich-text') {
                    contentHtml = data.content
                        .sort((a, b) => a.order - b.order)
                        .map(s => s.content)
                        .join('');
                }
            } else if (typeof data.content === 'string') {
                contentHtml = data.content;
            }

            setFormData({
                title: data.title || '',
                description: data.description || '',
                slug: data.slug || '',
                content: contentHtml,
                category_id: data.category_id || '',
                author_id: data.author_id || '',
                is_featured: data.is_featured || false,
                published_at: data.published_at
                    ? new Date(data.published_at).toISOString().slice(0, 16)
                    : '',
            });
        };
        fetchArticle();
    }, [articleId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const generateSlug = () => {
        const slug = formData.title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
        setFormData(prev => ({ ...prev, slug }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setStatus(null);

        try {
            if (!formData.title || !formData.slug) {
                throw new Error('Title and slug are required');
            }

            // Store content as rich-text JSON format
            const contentJson = [{
                type: 'rich-text',
                content: formData.content,
                order: 0,
            }];

            const articleData = {
                title: formData.title,
                description: formData.description,
                slug: formData.slug,
                content: contentJson,
                category_id: formData.category_id || null,
                author_id: formData.author_id || null,
                is_featured: formData.is_featured,
                published_at: formData.published_at
                    ? new Date(formData.published_at).toISOString()
                    : null,
            };

            let error;
            if (articleId) {
                ({ error } = await supabaseAdmin
                    .from('articles')
                    .update(articleData)
                    .eq('id', articleId));
            } else {
                ({ error } = await supabaseAdmin
                    .from('articles')
                    .insert([articleData]));
            }

            if (error) throw error;

            setStatus({
                type: 'success',
                message: articleId ? 'Article updated successfully!' : 'Article created successfully!',
            });
        } catch (error) {
            console.error('Error saving article:', error);
            setStatus({ type: 'error', message: error.message || 'Failed to save article' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="article-editor">
            <h2>{articleId ? 'Edit Article' : 'New Article'}</h2>

            {status && (
                <div className={`admin-status ${status.type}`}>{status.message}</div>
            )}

            <form className="editor-form" onSubmit={handleSave}>
                <div className="form-group">
                    <label>Title</label>
                    <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Article title"
                        required
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Slug</label>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                placeholder="url-friendly-slug"
                                required
                                style={{ flex: 1 }}
                            />
                            <button type="button" className="btn-secondary" onClick={generateSlug}>
                                Auto
                            </button>
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Category</label>
                        <select name="category_id" value={formData.category_id} onChange={handleChange}>
                            <option value="">— Select Category —</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group">
                    <label>Description</label>
                    <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Short description / meta description"
                    />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Author</label>
                        <select name="author_id" value={formData.author_id} onChange={handleChange}>
                            <option value="">— Select Author —</option>
                            {authors.map(author => (
                                <option key={author.id} value={author.id}>{author.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Publish Date</label>
                        <input
                            type="datetime-local"
                            name="published_at"
                            value={formData.published_at}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div className="form-group form-checkbox">
                    <input
                        type="checkbox"
                        name="is_featured"
                        id="is_featured"
                        checked={formData.is_featured}
                        onChange={handleChange}
                    />
                    <label htmlFor="is_featured">Featured Article</label>
                </div>

                <div className="form-group">
                    <label>
                        Content (HTML)
                        <button
                            type="button"
                            className="btn-secondary"
                            style={{ marginLeft: '1rem' }}
                            onClick={() => setShowPreview(!showPreview)}
                        >
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </button>
                    </label>
                    <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleChange}
                        placeholder="<h2>Section Title</h2><p>Your content here...</p>"
                    />
                </div>

                {showPreview && (
                    <div className="content-preview" dangerouslySetInnerHTML={{ __html: formData.content }} />
                )}

                <div className="editor-actions">
                    <button type="button" className="btn-secondary" onClick={onBack}>
                        Back to List
                    </button>
                    <button type="submit" className="btn-primary" disabled={saving}>
                        {saving ? 'Saving...' : (articleId ? 'Update Article' : 'Create Article')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ArticleEditor;
